import { useEffect, useState } from 'react';

import type {
  BannerFlagKey,
  BannerFlagValue,
} from 'components/common/Banner/Banner.types';

const DISMISSED_STORAGE_KEY = 'banner-dismissed';

// djb2a hash: small non-crypto fingerprint of banner content used as a localStorage key.
// Returns a short base-36 string (max 7 chars). Collisions are irrelevant at this scale.
const hashContent = (s: string): string => {
  // 5381 is the djb2 magic seed — empirically chosen for good distribution on short strings.
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    // XOR with the char code mixes bits in (djb2a variant).
    hash = ((hash << 5) + hash) ^ s.charCodeAt(i);
  }
  // >>> 0 coerces to unsigned 32-bit (JS bit ops are signed); base-36 keeps the output compact.
  return (hash >>> 0).toString(36);
};

const readDismissedMap = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeDismissedHash = (flagKey: BannerFlagKey, hash: string) => {
  try {
    const map = readDismissedMap();
    map[flagKey] = hash;
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage unavailable or quota exceeded — dismiss still works for the session
  }
};

export const useBannerDismissed = (flagKey: BannerFlagKey, banner: BannerFlagValue | undefined) => {
  const contentHash = banner
    ? hashContent(`${banner.type}|${banner.title}|${banner.message}`)
    : undefined;

  const isStoredAsDismissed = () =>
    !!contentHash && readDismissedMap()[flagKey] === contentHash;

  const [dismissed, setDismissed] = useState(isStoredAsDismissed);

  useEffect(() => {
    setDismissed(isStoredAsDismissed());
  }, [flagKey, contentHash]);

  const dismiss = () => {
    if (contentHash) writeDismissedHash(flagKey, contentHash);
    setDismissed(true);
  };

  return { dismissed, dismiss };
};
